import { ZONA_DE_OPERACION, formatFechaEnZona } from '@/shared/lib/dateUtils'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState, useRef, useEffect, useCallback } from "react";
import SelectBuscable from '@/shared/components/SelectBuscable';
import { comoEventoDeCambio } from '@/shared/lib/opcionesDeSelect';
import { comprimirImagen } from "@/shared/lib/imagenes";
import {
  ArrowLeft,
  Upload,
  X,
  Trash2,
  FileWarning,
  Pencil,
  Save,
  ImageIcon,
  Loader2,
  RefreshCw,
  CornerDownLeft,
  Folder,
  User,
  CheckCheck,
  Ban,
  ArrowRight,
  ChevronRight,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import PhotoViewerModal from '@/features/almacen/components/PhotoViewerModal';
import { apiService } from '@/services/core/api.service';
import type { UserProfile } from '@/services/api';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { ALMACEN } from '@/constants/permissions';
import toast from "react-hot-toast";
import { EsqueletoDetalle } from "@/shared/components/Esqueleto";
import { Checkbox } from "@/shared/components/Checkbox";
import {
  CargaDeDocumentos,
  DocumentosEnConsulta,
  SeccionDocumentos,
} from "@/features/almacen/components/DocumentosHerramienta";
import { UltimaEntrega } from "@/features/almacen/components/UltimaEntrega";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { GrupoDeEvidencia } from "@/features/almacen/components/GrupoDeEvidencia";
import { SelloDeAceptacion } from "@/features/almacen/components/SelloDeAceptacion";
import { RUTA_DE_RESPONSABLES } from "@/features/almacen/lib/responsablesDeAlmacen";
import { Badge, type ColorDeBadge } from "@/shared/components/Badge";
import { accionDelMovimiento } from "@/features/almacen/lib/retornoPorRechazo";
import { ResponsableDeHerramienta } from "@/features/almacen/components/ResponsableDeHerramienta";
import type {
  DocumentoAdjunto,
  DocumentoGuardado,
} from "@/features/almacen/lib/documentos";

// ─── Types ────────────────────────────────────────────────────────────────────

type Mode = "create" | "view" | "edit";
type EstadoHerramienta =
  | "En buen estado"
  | "Equipo nuevo"
  | "Falla/daño"
  | "Faltante"
  | "En mantenimiento"
  // La API lo devuelve desde siempre; aquí faltaba, y como el respaldo del
  // mapeo era "En buen estado", una herramienta dada de baja se mostraba como
  // si estuviera lista para usarse (A-09).
  | "Dada de baja";
type Disponibilidad = "Disponible" | "No disponible";

interface HerramientaFormProps {
  mode: Mode;
  id?: string;
  onBack?: () => void;
}

interface FormData {
  nombre: string;
  categoria: string;
  descripcion: string;
  numeroSerie: string;
  internalCode: string;
  disponibilidad: Disponibilidad | "";
  estado: EstadoHerramienta | "";
  proyectoAsignado: string;
  responsable: string;
  /** Quien dio de alta la herramienta. Vacio en las registradas antes de que se guardara. */
  registradaPor: string;
  /** CR-03: la asignación vigente espera que el receptor acepte. */
  estadoDeAprobacion?: "pending" | "approved" | "rejected" | null;
  pendienteDe?: string | null;
  almacen: string;
  /** Periodo de mantenimiento en meses. Vacio = sin mantenimiento periodico. */
  periodoMantenimiento: string;
  fotos: string[];
  /** Si la herramienta lleva fotos de evidencia de su estado (CR-04). */
  pideFotos: boolean;
  /** Documentos adjuntos de la ficha (CR-01). */
  documentos: DocumentoGuardado[];
  /** Notas libres de la ficha (CR-01). */
  informacionAdicional: string;
  createdAt?: string;
}

interface ApiWarehouse {
  id: string;
  name: string;
}

interface ApiToolCategory {
  id: string;
  name: string;
}

interface ApiProject {
  id: string;
  name: string;
}

// ─── Mock data ────────────────────────────────────────────────────────────────


const ESTADOS: EstadoHerramienta[] = [
  "En buen estado",
  "Equipo nuevo",
  "Falla/daño",
  "Faltante",
  "En mantenimiento",
];
const DISPONIBILIDADES: Disponibilidad[] = ["Disponible", "No disponible"];

// Periodo de mantenimiento (en meses). Se envía como number con tipo "calendar".
const PERIODOS_MANTENIMIENTO = ["1", "3", "6", "9", "12"];

// El backend guarda el intervalo de calendario en DIAS (evalua con
// setDate(+intervalValue)), mientras que la UI lo maneja en meses. 30 dias por
// mes es la conversion que ya asume el back en sus pruebas (3 meses = 90).
const DIAS_POR_MES = 30;

const EMPTY_FORM: FormData = {
  nombre: "",
  categoria: "",
  descripcion: "",
  numeroSerie: "",
  internalCode: "",
  disponibilidad: "",
  estado: "",
  proyectoAsignado: "",
  responsable: "",
  registradaPor: "",
  estadoDeAprobacion: null,
  pendienteDe: null,
  almacen: "",
  periodoMantenimiento: "",
  fotos: [],
  pideFotos: true,
  documentos: [],
  informacionAdicional: "",
};

// ─── Shared styles ────────────────────────────────────────────────────────────

const INPUT_CLASS =
  "w-full px-3 py-2.5 border-0 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 transition-colors bg-[#F6F9FB]";


// ── Estilos exactos del detalle (Figma · Albert Sans) ──
const SECTION_TITLE_STYLE: React.CSSProperties = {
  fontFamily: "'Albert Sans', sans-serif",
  fontWeight: 500,
  fontSize: 12,
  lineHeight: "100%",
  letterSpacing: 0,
  textTransform: "uppercase",
  color: "#979797",
};
const FIELD_LABEL_STYLE: React.CSSProperties = {
  fontFamily: "'Albert Sans', sans-serif",
  fontWeight: 500,
  fontSize: 12,
  lineHeight: "100%",
  color: "#777777",
};
const FIELD_VALUE_STYLE: React.CSSProperties = {
  fontFamily: "'Albert Sans', sans-serif",
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "100%",
  color: "#414141",
};

// Título de sub-sección: label (SECTION_TITLE_STYLE) + línea opcional que
// continúa a la derecha, centrada a media altura del texto. "Identificación"
// va sin línea (withLine=false); las demás con línea.
const GroupTitle = ({
  children,
  withLine = true,
}: {
  children: React.ReactNode;
  withLine?: boolean;
}) => (
  <div className="flex items-center gap-3 mb-4">
    <p className="whitespace-nowrap" style={SECTION_TITLE_STYLE}>
      {children}
    </p>
    {withLine && (
      <div className="flex-1 border-t" style={{ borderColor: "#E0E0E0" }} />
    )}
  </div>
);

// ─── Estado badge ─────────────────────────────────────────────────────────────

const EstadoBadge = ({ estado }: { estado: EstadoHerramienta }) => {
  const color: Record<EstadoHerramienta, ColorDeBadge> = {
    "En buen estado": "verde",
    "Equipo nuevo": "azul",
    "Falla/daño": "rojo",
    Faltante: "amarillo",
    "En mantenimiento": "amarillo",
    "Dada de baja": "gris",
  };
  return <Badge color={color[estado]}>{estado}</Badge>;
};

const DisponibilidadBadge = ({ value }: { value: Disponibilidad }) => (
  <Badge color={value === "Disponible" ? "verde" : "rojo"}>{value}</Badge>
);

// ─── Photo upload card (edit/create) ─────────────────────────────────────────

const PhotoCard = ({
  preview,
  uploading,
  onFile,
  onRemove,
}: {
  preview: string | null;
  uploading?: boolean;
  onFile: (file: File) => void;
  onRemove: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-2" style={{ width: 150 }}>
      {preview ? (
        <div
          className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50"
          style={{ width: 150, height: 150 }}
        >
          <img
            src={preview}
            alt="Foto"
            className="w-full h-full object-contain"
          />
          {uploading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <span className="text-xs text-gray-500">Subiendo...</span>
            </div>
          )}
          {!uploading && (
            <button
              type="button"
              onClick={onRemove}
              className="absolute top-2 right-2 bg-white rounded-full p-0.5 shadow hover:bg-gray-100 transition-colors"
            >
              <X className="w-3.5 h-3.5 text-gray-600" />
            </button>
          )}
        </div>
      ) : (
        <div
          className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center"
          style={{ width: 150, height: 150 }}
        >
          {uploading ? (
            <span className="text-xs text-gray-400">Subiendo...</span>
          ) : (
            <ImageIcon className="w-8 h-8 text-gray-300" />
          )}
        </div>
      )}
      <button
        type="button"
        disabled={uploading || !!preview}
        onClick={() => inputRef.current?.click()}
        className={estiloDeBoton("primario")}
      >
        <Upload className="w-4 h-4" />
        Cargar
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          // Reset input para permitir seleccionar el mismo archivo de nuevo
          e.target.value = "";
        }}
      />
    </div>
  );
};

// ─── View mode ────────────────────────────────────────────────────────────────

interface MovementDto {
  id: string;
  type: "assignment" | "reassignment" | "return" | "decommission";
  responsibleUserName: string | null;
  performedByUserName: string | null;
  projectName: string | null;
  resultingStatus: string;
  createdAt: string;
  evidenceUrl1?: string | null;
  evidenceUrl2?: string | null;
  evidenceUrl3?: string | null;
  /** Documentos que respaldan el movimiento (CR-01). Hoy solo los trae la baja. */
  documents?: DocumentoGuardado[];
  /** Motivo, solo en los movimientos de baja. */
  decommissionReason?: string | null;
  /** Nota de quien entregó la herramienta. */
  notes?: string | null;
  /** Aprobación del receptor (CR-03). Nula en asignaciones anteriores al flujo. */
  approvalStatus?: "pending" | "approved" | "rejected" | null;
  signatureUrl?: string | null;
  rejectionReason?: string | null;
  /**
   * Fotos que toma quien recibe al aceptar o rechazar (A-01).
   *
   * La API las devuelve desde que existe el flujo de aprobación; aquí no se
   * declaraban, así que se perdían al leer la respuesta y el movimiento se
   * mostraba sin evidencia aunque el receptor hubiera tomado fotos.
   */
  approvalEvidenceUrl1?: string | null;
  approvalEvidenceUrl2?: string | null;
  approvalEvidenceUrl3?: string | null;
  /**
   * El sello de la aceptación (A-02). `approvalResolvedAt` es hora del
   * servidor y es la que vale; la del dispositivo puede ser horas antes si se
   * aceptó sin conexión.
   */
  approvalResolvedAt?: string | null;
  approvalDeviceTimestamp?: string | null;
  approvalIp?: string | null;
  termsVersion?: string | null;
  termsVersionTitle?: string | null;
  /** El rechazo que provocó este retorno, si lo es (A-08). */
  rejectedMovementId?: string | null;
}

const MOVEMENT_LABEL: Record<string, string> = {
  assignment: "Asignación",
  reassignment: "Reasignación",
  return: "Devolución",
  decommission: "Baja",
};

const STATUS_LABEL: Record<string, EstadoHerramienta> = {
  good: "En buen estado",
  new_equipment: "Equipo nuevo",
  damaged: "Falla/daño",
  in_maintenance: "En mantenimiento",
  missing: "Faltante",
  decommissioned: "Dada de baja",
};

// ─── Reporte de daño modal ────────────────────────────────────────────────────

const TIPOS_REPORTE = [
  { label: "Daño físico / avería", value: "damaged" },
  { label: "Pérdida", value: "missing" },
];

export const ReporteModal = ({
  toolId,
  toolName,
  onClose,
  onCreated,
}: {
  // Opcionales: desde el listado de incidencias no hay herramienta elegida
  // todavia y el propio modal la pide.
  toolId?: string;
  toolName?: string;
  onClose: () => void;
  onCreated?: (reportId: string) => void;
}) => {
  const [tools, setTools] = useState<{ id: string; name: string; serialCode: string }[]>([]);
  const [toolSel, setToolSel] = useState(toolId ?? "");
  const eligeHerramienta = !toolId;
  const effToolId = toolId ?? toolSel;
  const [projects, setProjects] = useState<ApiProject[]>([]);
  const [proyecto, setProyecto] = useState("");
  const [tipo, setTipo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [evidences, setEvidences] = useState<(string | null)[]>([
    null,
    null,
    null,
  ]);
  const [evidencesUploading, setEvidencesUploading] = useState<boolean[]>([
    false,
    false,
    false,
  ]);
  const [saving, setSaving] = useState(false);
  // Evita reportes duplicados: si la herramienta ya tiene una incidencia
  // abierta, se bloquea el formulario y se enlaza al reporte existente.
  const [existingOpen, setExistingOpen] = useState<{
    id: string;
    folioDisplay: string;
  } | null>(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  // CR-04: la ficha de la herramienta decide si se le piden fotos al moverla.
  // Se consulta aparte porque este modal se abre desde tres pantallas y solo
  // una de ellas trae los datos de la herramienta.
  const [pideFotos, setPideFotos] = useState(true);
  const navigate = useNavigate();

  const inputRef0 = useRef<HTMLInputElement>(null);
  const inputRef1 = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);
  const inputRefs = [inputRef0, inputRef1, inputRef2];

  useEffect(() => {
    apiService
      .request<{ data: ApiProject[] } | ApiProject[]>("/projects/public/all")
      .then((proj) => {
        const p = Array.isArray(proj)
          ? proj
          : ((proj as { data: ApiProject[] }).data ?? []);
        setProjects(p);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!eligeHerramienta) return;
    apiService
      .request<{ data: { id: string; name: string; serialCode: string }[] }>(
        "/almacen/herramientas?limit=100",
      )
      .then((res) => setTools(res.data ?? []))
      .catch(() => {});
  }, [eligeHerramienta]);

  // Una herramienta no puede tener dos incidencias abiertas: el back rechaza la
  // segunda, asi que se avisa antes de que llene el formulario.
  useEffect(() => {
    if (!effToolId) {
      setExistingOpen(null);
      setCheckingExisting(false);
      return;
    }
    setCheckingExisting(true);
    apiService
      .request<{ data: { id: string; folioDisplay: string }[] }>(
        `/almacen/reportes?toolId=${effToolId}&status=open&limit=1`,
      )
      .then((res) => setExistingOpen(res.data?.[0] ?? null))
      .catch(() => {})
      .finally(() => setCheckingExisting(false));
  }, [effToolId]);

  useEffect(() => {
    if (!effToolId) {
      setPideFotos(true);
      return;
    }
    apiService
      .request<{ requiresConditionPhotos?: boolean }>(
        `/almacen/herramientas/${effToolId}`,
      )
      // Si no se pudo saber, se piden: es lo que el back va a exigir de vuelta.
      .then((h) => setPideFotos(h.requiresConditionPhotos !== false))
      .catch(() => setPideFotos(true));
  }, [effToolId]);

  const handleFile =
    (index: number) => async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";
      // Preview local inmediato
      const localPreview = URL.createObjectURL(file);
      setEvidences((prev) =>
        prev.map((v, i) => (i === index ? localPreview : v)),
      );
      setEvidencesUploading((prev) =>
        prev.map((v, i) => (i === index ? true : v)),
      );
      try {
        const formData = new globalThis.FormData();
        formData.append("file", await comprimirImagen(file));
        const res = await apiService.request<{ url: string }>(
          "/almacen/upload/foto",
          {
            method: "POST",
            body: formData,
          },
        );
        URL.revokeObjectURL(localPreview);
        setEvidences((prev) => prev.map((v, i) => (i === index ? res.url : v)));
      } catch {
        URL.revokeObjectURL(localPreview);
        setEvidences((prev) => prev.map((v, i) => (i === index ? null : v)));
        toast.error("Error al subir la foto. Intenta de nuevo.");
      } finally {
        setEvidencesUploading((prev) =>
          prev.map((v, i) => (i === index ? false : v)),
        );
      }
    };

  const anyEvidenceUploading = evidencesUploading.some(Boolean);

  const handleSubmit = async () => {
    if (saving || anyEvidenceUploading) return;
    setSaving(true);
    try {
      if (!effToolId) {
        toast.error("Selecciona la herramienta.");
        return;
      }
      if (!tipo) {
        toast.error("Selecciona el tipo de reporte.");
        return;
      }
      if (!proyecto) {
        toast.error("Selecciona el proyecto.");
        return;
      }
      // Filtrar blob:// por si acaso (no deberían existir con el upload correcto)
      const realEvidences = evidences.map((u) =>
        u && !u.startsWith("blob:") ? u : null,
      );
      // La evidencia es obligatoria salvo para reportes de pérdida (missing) y
      // salvo que la ficha de la herramienta no pida fotos (CR-04).
      if (pideFotos && tipo !== "missing" && !realEvidences.some(Boolean)) {
        toast.error("Se requiere al menos una imagen de evidencia.");
        return;
      }
      const created = await apiService.request<{ id: string }>(
        `/almacen/reportes`,
        {
          method: "POST",
          body: JSON.stringify({
            toolId: effToolId,
            projectId: proyecto,
            type: tipo,
            ...(descripcion.trim() && { comment: descripcion.trim() }),
            ...(realEvidences[0] && { evidenceUrl1: realEvidences[0] }),
            ...(realEvidences[1] && { evidenceUrl2: realEvidences[1] }),
            ...(realEvidences[2] && { evidenceUrl3: realEvidences[2] }),
          }),
        },
      );
      toast.success("Reporte guardado");
      onClose();
      onCreated?.(created.id);
    } catch (err: unknown) {
      toast.error(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <CapaDeModal>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Nueva incidencia
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {eligeHerramienta
                ? "Elige la herramienta y describe la incidencia"
                : `Herramienta: ${toolName}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4 mt-0.5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {existingOpen && (
          <div className="mx-6 mb-4 rounded-lg border px-4 py-3 flex items-start gap-3" style={{ borderColor: "#F5C6CB", backgroundColor: "#FBEEEE" }}>
            <FileWarning className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#F15C5C" }} />
            <p className="text-sm text-gray-700">
              Esta herramienta ya tiene una incidencia abierta (
              {existingOpen.folioDisplay}). Resuélvela antes de crear una
              nueva.{" "}
              <button
                type="button"
                onClick={() => navigate(`/almacen/reportes/${existingOpen.id}/ver`)}
                className="font-semibold text-[#315FB9] hover:underline"
              >
                Ver reporte
              </button>
            </p>
          </div>
        )}

        <div className="px-6 space-y-4">
          {eligeHerramienta && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Herramienta <span className="text-red-500">*</span>
              </label>
              <SelectBuscable
                variante="formulario"
                opciones={tools.map((t) => ({ value: String(t.id), label: `${t.name}${t.serialCode ? ` · ${t.serialCode}` : ""}` }))}
                value={toolSel}
                onChange={setToolSel}
                placeholder="Seleccionar herramienta"
                placeholderDeBusqueda="Buscar herramienta..."
              />
            </div>
          )}

          {/* Selects */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Proyecto <span className="text-red-500">*</span>
              </label>
              <SelectBuscable
                variante="formulario"
                opciones={projects.map((p) => ({ value: String(p.id), label: p.name }))}
                value={proyecto}
                onChange={setProyecto}
                placeholder="Seleccionar"
                placeholderDeBusqueda="Buscar proyecto..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de incidencia <span className="text-red-500">*</span>
              </label>
              <SelectBuscable
                variante="formulario"
                opciones={TIPOS_REPORTE.map((t) => ({ value: t.value, label: t.label }))}
                value={tipo}
                onChange={setTipo}
                placeholder="Seleccionar"
                placeholderDeBusqueda="Buscar tipo..."
              />
            </div>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              rows={3}
              placeholder="Ingresa la descripción"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>

          {/* Evidencia fotográfica */}
          <div>
            <p className="text-base font-semibold text-gray-900 mb-1">
              Evidencia fotográfica
            </p>
            <p
              className={`text-sm mb-3 ${
                tipo === "missing" || !pideFotos
                  ? "text-gray-500"
                  : "text-[#DC2626]"
              }`}
            >
              {tipo === "missing"
                ? "Opcional para reportes de pérdida."
                : !pideFotos
                  ? "Opcional para esta herramienta."
                  : "Se requiere al menos una imagen."}
            </p>
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-lg border px-4 py-2.5"
                  style={{ borderColor: "#E0E0E0" }}
                >
                  <input
                    ref={inputRefs[i]}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFile(i)}
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {evidencesUploading[i] ? (
                      <div className="w-5 h-5 rounded-full border-2 border-[#315FB9] border-t-transparent animate-spin flex-shrink-0" />
                    ) : evidences[i] ? (
                      <img
                        src={evidences[i]!}
                        alt=""
                        className="w-8 h-8 rounded object-cover flex-shrink-0"
                      />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-sm text-gray-500 truncate">
                      {evidencesUploading[i]
                        ? "Subiendo…"
                        : evidences[i]
                          ? "Imagen cargada"
                          : "Cargar imagen"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => inputRefs[i].current?.click()}
                    disabled={evidencesUploading[i]}
                    className={estiloDeBoton("primario", "flex-shrink-0")}
                  >
                    <Upload className="w-4 h-4" />
                    Cargar
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-5">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={estiloDeBoton("secundario")}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || anyEvidenceUploading || checkingExisting || !!existingOpen}
            className={estiloDeBoton("primario")}
          >
            <Save className="w-4 h-4" />
            {saving
              ? "Creando…"
              : anyEvidenceUploading
                ? "Subiendo fotos…"
                : "Crear incidencia"}
          </button>
        </div>
      </div>
    </CapaDeModal>
  );
};

// ─── Dar de baja modal ─────────────────────────────────────────────────────────

const MOTIVOS_BAJA = [
  { label: "Robo", value: "theft" },
  { label: "Extravío", value: "loss" },
  { label: "Daño irreparable", value: "irreparable_damage" },
  { label: "Fin de vida útil", value: "end_of_life" },
];

/** Motivo de baja como lo lee una persona. La clave sale del backend. */
const ETIQUETA_MOTIVO_BAJA: Record<string, string> = Object.fromEntries(
  MOTIVOS_BAJA.map((m) => [m.value, m.label]),
);

export const DarBajaModal = ({
  toolId,
  toolName,
  onClose,
  onDone,
}: {
  toolId: string;
  toolName: string;
  onClose: () => void;
  onDone: () => void;
}) => {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [evidences, setEvidences] = useState<(string | null)[]>([
    null,
    null,
    null,
  ]);
  const [evidencesUploading, setEvidencesUploading] = useState<boolean[]>([
    false,
    false,
    false,
  ]);
  /**
   * Documentos que respaldan la baja (CR-01). Quedan ligados al movimiento de
   * baja, no a la ficha: son evidencia de lo que paso ese dia.
   */
  const [documentos, setDocumentos] = useState<DocumentoAdjunto[]>([]);
  const [documentosSubiendo, setDocumentosSubiendo] = useState(false);
  const [saving, setSaving] = useState(false);

  const inputRef0 = useRef<HTMLInputElement>(null);
  const inputRef1 = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);
  const inputRefs = [inputRef0, inputRef1, inputRef2];

  const handleFile =
    (index: number) => async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";
      const localPreview = URL.createObjectURL(file);
      setEvidences((prev) =>
        prev.map((v, i) => (i === index ? localPreview : v)),
      );
      setEvidencesUploading((prev) =>
        prev.map((v, i) => (i === index ? true : v)),
      );
      try {
        const formData = new globalThis.FormData();
        formData.append("file", await comprimirImagen(file));
        const res = await apiService.request<{ url: string }>(
          "/almacen/upload/foto",
          { method: "POST", body: formData },
        );
        URL.revokeObjectURL(localPreview);
        setEvidences((prev) => prev.map((v, i) => (i === index ? res.url : v)));
      } catch {
        URL.revokeObjectURL(localPreview);
        setEvidences((prev) => prev.map((v, i) => (i === index ? null : v)));
        toast.error("Error al subir la foto. Intenta de nuevo.");
      } finally {
        setEvidencesUploading((prev) =>
          prev.map((v, i) => (i === index ? false : v)),
        );
      }
    };

  const anyEvidenceUploading = evidencesUploading.some(Boolean);

  const handleSubmit = async () => {
    if (saving || anyEvidenceUploading || documentosSubiendo) return;
    if (!reason) {
      toast.error("Selecciona el motivo de la baja.");
      return;
    }
    setSaving(true);
    try {
      const realEvidences = evidences.map((u) =>
        u && !u.startsWith("blob:") ? u : null,
      );
      await apiService.request(`/almacen/herramientas/${toolId}/dar-baja`, {
        method: "POST",
        body: JSON.stringify({
          reason,
          ...(notes.trim() && { notes: notes.trim() }),
          ...(realEvidences[0] && { evidenceUrl1: realEvidences[0] }),
          ...(realEvidences[1] && { evidenceUrl2: realEvidences[1] }),
          ...(realEvidences[2] && { evidenceUrl3: realEvidences[2] }),
          ...(documentos.length > 0 && { documents: documentos }),
        }),
      });
      toast.success("Herramienta dada de baja");
      onDone();
    } catch (err: unknown) {
      // El backend regresa mensajes claros para 404/409 (ya dada de baja, o
      // tiene asignación activa); los mostramos tal cual.
      const e = err as { message?: string | string[] };
      const msg = Array.isArray(e.message)
        ? e.message.join(" ")
        : (e.message ?? "No se pudo dar de baja la herramienta.");
      toast.error(msg);
      setSaving(false);
    }
  };

  return (
    <CapaDeModal>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Dar de baja</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Retira del inventario la herramienta &ldquo;{toolName}&rdquo;.
              Esta acción no se puede deshacer.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4 mt-0.5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 space-y-4">
          {/* Motivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo de la baja <span className="text-red-500">*</span>
            </label>
            <SelectBuscable
              variante="formulario"
              opciones={MOTIVOS_BAJA.map((m) => ({ value: m.value, label: m.label }))}
              value={reason}
              onChange={setReason}
              placeholder="Seleccionar"
              placeholderDeBusqueda="Buscar motivo..."
            />
          </div>

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              rows={3}
              placeholder="Información adicional (opcional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>

          {/* Fotos de evidencia */}
          <div>
            <p className="text-base font-semibold text-gray-900 mb-1">
              Fotos de evidencia
            </p>
            <p className="text-sm mb-3 text-gray-500">Opcional.</p>
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-lg border px-4 py-2.5"
                  style={{ borderColor: "#E0E0E0" }}
                >
                  <input
                    ref={inputRefs[i]}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFile(i)}
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {evidencesUploading[i] ? (
                      <div className="w-5 h-5 rounded-full border-2 border-[#315FB9] border-t-transparent animate-spin flex-shrink-0" />
                    ) : evidences[i] ? (
                      <img
                        src={evidences[i]!}
                        alt=""
                        className="w-8 h-8 rounded object-cover flex-shrink-0"
                      />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-sm text-gray-500 truncate">
                      {evidencesUploading[i]
                        ? "Subiendo…"
                        : evidences[i]
                          ? "Imagen cargada"
                          : "Cargar imagen"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => inputRefs[i].current?.click()}
                    disabled={evidencesUploading[i]}
                    className={estiloDeBoton("primario", "flex-shrink-0")}
                  >
                    <Upload className="w-4 h-4" />
                    Cargar
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Documentos que respaldan la baja (CR-01) */}
          <div>
            <p className="text-base font-semibold text-gray-900 mb-1">
              Documentos
            </p>
            <p className="text-sm mb-3 text-gray-500">Opcional.</p>
            <CargaDeDocumentos
              documentos={documentos}
              onChange={setDocumentos}
              onSubiendoChange={setDocumentosSubiendo}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-5">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={estiloDeBoton("secundario")}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || anyEvidenceUploading || documentosSubiendo}
            className={estiloDeBoton("peligro")}
          >
            <Ban className="w-4 h-4" />
            {saving
              ? "Dando de baja…"
              : anyEvidenceUploading || documentosSubiendo
                ? "Subiendo archivos…"
                : "Dar de baja"}
          </button>
        </div>
      </div>
    </CapaDeModal>
  );
};

// ─── Dar de alta (reactivar) modal ──────────────────────────────────────────────

const ESTADOS_ALTA = [
  { label: "En buen estado", value: "good" },
  { label: "Equipo nuevo", value: "new_equipment" },
];

export const DarAltaModal = ({
  toolId,
  toolName,
  onClose,
  onDone,
}: {
  toolId: string;
  toolName: string;
  onClose: () => void;
  onDone: () => void;
}) => {
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (saving) return;
    if (!status) {
      toast.error("Selecciona el estado destino.");
      return;
    }
    setSaving(true);
    try {
      await apiService.request(`/almacen/herramientas/${toolId}/dar-alta`, {
        method: "POST",
        body: JSON.stringify({
          status,
          ...(notes.trim() && { notes: notes.trim() }),
        }),
      });
      toast.success("Herramienta reactivada");
      onDone();
    } catch (err: unknown) {
      const e = err as { message?: string | string[] };
      const msg = Array.isArray(e.message)
        ? e.message.join(" ")
        : (e.message ?? "No se pudo reactivar la herramienta.");
      toast.error(msg);
      setSaving(false);
    }
  };

  return (
    <CapaDeModal>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Dar de alta</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Reactiva la herramienta &ldquo;{toolName}&rdquo; y regrésala al
              inventario.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4 mt-0.5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado al reactivar <span className="text-red-500">*</span>
            </label>
            <SelectBuscable
              variante="formulario"
              opciones={ESTADOS_ALTA.map((s) => ({ value: s.value, label: s.label }))}
              value={status}
              onChange={setStatus}
              placeholder="Seleccionar"
              placeholderDeBusqueda="Buscar estado..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              rows={3}
              placeholder="Información adicional (opcional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-5">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={estiloDeBoton("secundario")}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className={estiloDeBoton("exito")}
          >
            <RefreshCw className="w-4 h-4" />
            {saving ? "Reactivando…" : "Dar de alta"}
          </button>
        </div>
      </div>
    </CapaDeModal>
  );
};

const ViewMode = ({
  data,
  id,
  onBack,
  onEdit,
  currentMovementId,
  warehouseId,
}: {
  data: FormData;
  id?: string;
  onBack?: () => void;
  onEdit: () => void;
  currentMovementId?: string | null;
  warehouseId?: string;
}) => {
  const [historial, setHistorial] = useState<MovementDto[]>([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [historialError, setHistorialError] = useState(false);
  // Historial de mantenimiento (entidad aparte de los movimientos)
  const [mantRows, setMantRows] = useState<
    {
      id: string;
      tipo: string;
      fechaEnvio: string;
      taller: string;
      responsable: string;
      realizadoPor: string;
      realizadoPorRetorno: string | null;
      estado: "Reparado" | "En mantenimiento";
      createdAt: string;
      evidenceUrl1: string | null;
      evidenceUrl2: string | null;
      evidenceUrl3: string | null;
    }[]
  >([]);
  const [loadingMant, setLoadingMant] = useState(false);
  const [mantError, setMantError] = useState(false);
  // Tab activo en la sección de fotos: herramienta vs evidencia
  const [photoTab, setPhotoTab] = useState<"herramienta" | "evidencia">(
    "herramienta",
  );
  /**
   * Fotos que debe mostrar el tab «Evidencia» del visor, cuando no son las de
   * la última evidencia.
   *
   * La entrega tiene sus propias fotos y no siempre son las mismas: el
   * movimiento más reciente con fotos puede ser una devolución o un
   * mantenimiento posterior. Sin esto, abrir una foto de la entrega mostraba
   * en grande la de otro movimiento.
   */
  const [fotosDelViewer, setFotosDelViewer] = useState<string[] | null>(null);
  // Visor de fotos ampliado
  const [viewer, setViewer] = useState<{
    tab: "herramienta" | "evidencia";
    index: number;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDarBajaModal, setShowDarBajaModal] = useState(false);
  const [showDarAltaModal, setShowDarAltaModal] = useState(false);
  // Incidencia abierta de esta herramienta. El back no deja crear una segunda,
  // asi que ofrecer Reportar aqui era ofrecer una accion que iba a fallar: lo
  // que hace falta es cerrar la que ya existe.
  const [reporteAbierto, setReporteAbierto] = useState<{
    id: string;
    folioDisplay: string;
  } | null>(null);
  const [revisandoReporte, setRevisandoReporte] = useState(true);

  // Una herramienta dada de baja sigue siendo consultable, pero ya no se le
  // hace nada: el detalle la mostraba como si estuviera en buen estado y con
  // todos los botones puestos (A-09).
  const estaDadaDeBaja = data.estado === "Dada de baja";

  const { hasPermission, isLoading: permsLoading } = usePermissions();
  const canDarBaja =
    !permsLoading && hasPermission(ALMACEN.HERRAMIENTAS_DAR_BAJA);

  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      setReporteAbierto(null);
      setRevisandoReporte(false);
      return;
    }
    setRevisandoReporte(true);
    apiService
      .request<{ data: { id: string; folioDisplay: string }[] }>(
        `/almacen/reportes?toolId=${id}&status=open&limit=1`,
      )
      .then((res) => setReporteAbierto(res.data?.[0] ?? null))
      // Si la consulta falla no se afirma que no hay incidencia: se deja sin
      // bandera y el back sigue siendo quien rechaza una segunda.
      .catch(() => setReporteAbierto(null))
      .finally(() => setRevisandoReporte(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setLoadingHistorial(true);
    setHistorialError(false);
    apiService
      .request<{ data: MovementDto[] }>(
        `/almacen/herramientas/${id}/movimientos?limit=50`,
      )
      .then((res) => setHistorial(res.data ?? []))
      .catch(() => setHistorialError(true))
      .finally(() => setLoadingHistorial(false));
  }, [id]);

  // Historial de mantenimiento: endpoint aparte.
  //
  // Quién realizó la entrada y el retorno los resuelve el back por su relación
  // con el usuario. El responsable sigue cruzándose con /users/public/all
  // porque el DTO de mantenimiento solo trae su id; ese endpoint filtra activos,
  // así que un responsable dado de baja aparece como Sin responsable.
  useEffect(() => {
    if (!id) return;
    setLoadingMant(true);
    setMantError(false);
    Promise.all([
      apiService.request<{
        data: {
          id: string;
          type: string;
          destination: string | null;
          responsibleUserId: string | null;
          performedByUserId: string | null;
          performedByUserName: string | null;
          returnPerformedByUserId: string | null;
          returnPerformedByUserName: string | null;
          sentAt: string | null;
          returnedAt: string | null;
          createdAt: string;
          evidenceUrl1?: string | null;
          evidenceUrl2?: string | null;
          evidenceUrl3?: string | null;
        }[];
      }>(`/almacen/herramientas/${id}/mantenimiento?limit=50`),
      apiService.request<
        { id: string; firstName: string; lastName: string }[]
      >("/users/public/all"),
    ])
      .then(([mRes, users]) => {
        const usersMap: Record<string, string> = {};
        for (const u of users) usersMap[u.id] = `${u.firstName} ${u.lastName}`;
        setMantRows(
          (mRes.data ?? [])
            // Solo los 10 más recientes en el detalle; el resto en la pantalla de Mantenimiento.
            .slice()
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )
            .slice(0, 10)
            .map((m) => ({
              id: m.id,
              tipo: m.type === "corrective" ? "Correctivo" : "Preventivo",
              fechaEnvio: formatFechaEnZona(m.sentAt ? String(m.sentAt) : null),
              taller: m.destination ?? "Sin taller",
              responsable: m.responsibleUserId
                ? (usersMap[m.responsibleUserId] ?? "Sin responsable")
                : "Sin responsable",
              realizadoPor: m.performedByUserName ?? "Sin registro",
              realizadoPorRetorno: m.returnPerformedByUserId
                ? (m.returnPerformedByUserName ?? "Sin registro")
                : null,
              estado: m.returnedAt ? "Reparado" : "En mantenimiento",
              createdAt: m.createdAt,
              evidenceUrl1: m.evidenceUrl1 ?? null,
              evidenceUrl2: m.evidenceUrl2 ?? null,
              evidenceUrl3: m.evidenceUrl3 ?? null,
            })),
        );
      })
      .catch(() => setMantError(true))
      .finally(() => setLoadingMant(false));
  }, [id]);

  const handleDelete = async () => {
    if (!id || deleting) return;
    setDeleting(true);
    try {
      // Si tiene asignación activa, registrar devolución primero
      if (currentMovementId) {
        await apiService.request(`/almacen/herramientas/${id}/devolver`, {
          method: "POST",
          body: JSON.stringify({
            resultingStatus: "good",
            ...(warehouseId && { warehouseId }),
          }),
        });
      }
      await apiService.request(`/almacen/herramientas/${id}`, {
        method: "DELETE",
      });
      onBack?.();
    } catch (err: unknown) {
      toast.error(friendlyError(err));
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  // Movimientos ordenados por fecha desc; en el detalle solo mostramos los 10
  // más recientes (el resto vive en el historial completo).
  const movimientosOrdenados = [...historial].sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const movimientosRecientes = movimientosOrdenados.slice(0, 10);

  // Documentos que respaldan un movimiento (CR-01). Salen del mismo historial
  // que ya se pidió, así que no cuestan una petición extra.
  const adjuntosDeMovimientos = movimientosOrdenados
    .filter((m) => (m.documents?.length ?? 0) > 0)
    .map((m) => ({
      etiqueta: `Documentos de la ${(MOVEMENT_LABEL[m.type] ?? m.type).toLowerCase()}`,
      documentos: m.documents as DocumentoGuardado[],
    }));

  // Última evidencia: el registro más reciente con fotos, sea un movimiento
  // (asignación, devolución, reporte, etc.) o un envío a mantenimiento —
  // son entidades aparte, así que hay que comparar ambas fuentes.
  const evidenciaMovimientos = movimientosOrdenados
    .map((m) => ({
      fecha: new Date(m.createdAt).getTime(),
      urls: [m.evidenceUrl1, m.evidenceUrl2, m.evidenceUrl3].filter(
        (u): u is string => !!u,
      ),
      // Las que toma quien recibe al aceptar o rechazar (A-01). Se guardaban
      // desde siempre; la pantalla no las leía y el movimiento se veía sin
      // evidencia aunque el receptor hubiera tomado fotos.
      urlsDelReceptor: [
        m.approvalEvidenceUrl1,
        m.approvalEvidenceUrl2,
        m.approvalEvidenceUrl3,
      ].filter((u): u is string => !!u),
    }))
    .filter((m) => m.urls.length > 0 || m.urlsDelReceptor.length > 0);
  const evidenciaMantenimiento = mantRows
    .map((m) => ({
      fecha: new Date(m.createdAt).getTime(),
      urls: [m.evidenceUrl1, m.evidenceUrl2, m.evidenceUrl3].filter(
        (u): u is string => !!u,
      ),
      // Un mantenimiento no tiene receptor que firme.
      urlsDelReceptor: [] as string[],
    }))
    .filter((m) => m.urls.length > 0);

  const ultimaEvidencia = [
    ...evidenciaMovimientos,
    ...evidenciaMantenimiento,
  ].sort((a, b) => b.fecha - a.fecha)[0];

  const evidenciaDeQuienEntrega = ultimaEvidencia?.urls ?? [];
  const evidenciaDeQuienRecibe = ultimaEvidencia?.urlsDelReceptor ?? [];
  // El visor recorre una sola lista, así que van en el mismo orden en que se
  // pintan: primero las de quien entrega.
  const recentEvidenceFotos = [
    ...evidenciaDeQuienEntrega,
    ...evidenciaDeQuienRecibe,
  ];

  /**
   * La entrega más reciente: la última asignación o reasignación.
   *
   * Se muestra solo si dejó algo que enseñar —firma, nota o fotos—. Una
   * asignación pelona no agrega nada a lo que ya dice «Movimientos recientes»,
   * y una tarjeta vacía entre dos que sí tienen contenido se lee como un error.
   */
  const ultimaEntrega = movimientosOrdenados.find(
    (m) =>
      (m.type === "assignment" || m.type === "reassignment") &&
      (!!m.signatureUrl ||
        !!m.notes?.trim() ||
        !!m.rejectionReason?.trim() ||
        !!(m.evidenceUrl1 || m.evidenceUrl2 || m.evidenceUrl3) ||
        !!(
          m.approvalEvidenceUrl1 ||
          m.approvalEvidenceUrl2 ||
          m.approvalEvidenceUrl3
        )),
  );
  const fotosDeLaEntrega = ultimaEntrega
    ? [
        ultimaEntrega.evidenceUrl1,
        ultimaEntrega.evidenceUrl2,
        ultimaEntrega.evidenceUrl3,
      ].filter((u): u is string => !!u)
    : [];
  const fotosDelReceptorEnLaEntrega = ultimaEntrega
    ? [
        ultimaEntrega.approvalEvidenceUrl1,
        ultimaEntrega.approvalEvidenceUrl2,
        ultimaEntrega.approvalEvidenceUrl3,
      ].filter((u): u is string => !!u)
    : [];

  // Navega al historial completo de esta herramienta (preselecciona la tool)
  const goToHistorial = () =>
    navigate(`/almacen/historial/herramientas/${id}`, {
      state: {
        preselectTool: {
          id,
          nombre: data.nombre,
          codigo: data.numeroSerie,
          internalCode:
            data.internalCode === "Sin código interno" ? "" : data.internalCode,
          fecha: "",
          actividad: "Reciente",
          photoUrl: null,
          dadaDeBaja: estaDadaDeBaja,
        },
      },
    });

  return (
    <div className="mx-auto px-6 py-8 max-w-7xl">
      {/* Visor de fotos ampliado */}
      <PhotoViewerModal
        open={viewer !== null}
        onClose={() => {
          setViewer(null);
          setFotosDelViewer(null);
        }}
        toolPhotos={data.fotos}
        evidencePhotos={fotosDelViewer ?? recentEvidenceFotos}
        initialTab={viewer?.tab ?? "herramienta"}
        initialIndex={viewer?.index ?? 0}
      />

      {/* Confirm delete modal */}
      {confirmDelete && (
        <CapaDeModal>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              ¿Eliminar herramienta?
            </h3>
            {currentMovementId ? (
              <p className="text-sm text-gray-500 mb-5">
                <span className="font-medium text-gray-700">
                  "{data.nombre}"
                </span>{" "}
                tiene una asignación activa. Se registrará la devolución
                automáticamente y luego se eliminará. ¿Deseas continuar? Esta
                accion no se puede deshacer.
              </p>
            ) : (
              <p className="text-sm text-gray-500 mb-5">
                Esta acción dará de baja{" "}
                <span className="font-medium text-gray-700">
                  "{data.nombre}"
                </span>
                . No se puede deshacer.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className={estiloDeBoton("secundario")}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className={estiloDeBoton("peligro")}
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Eliminando...
                  </>
                ) : currentMovementId ? (
                  "Devolver y eliminar"
                ) : (
                  "Sí, eliminar"
                )}
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            className={estiloDeBoton("secundario")}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">{data.nombre}</h1>
              {reporteAbierto && (
                <Badge color="rojo">
                  Con reporte abierto · {reporteAbierto.folioDisplay}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {estaDadaDeBaja
                ? "Esta herramienta está dada de baja: puedes consultar su historial o darla de alta otra vez"
                : "Consulta el estado de esta herramienta, asígnala, devuélvela o reporta un problema"}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-1">
          {/* Acciones principales. Sobre una herramienta dada de baja no se
              ofrecen: editarla, asignarla, devolverla o reportarla son cosas
              que el back rechaza, y darla de baja otra vez no significa nada
              (A-09). Lo que queda es reactivarla o eliminarla. */}
          {!estaDadaDeBaja && (
          <button
            type="button"
            onClick={onEdit}
            className={estiloDeBoton("primario")}
          >
            <Pencil className="w-4 h-4" />
            Editar
          </button>
          )}
          {/* Acción según estado: En mantenimiento → Devolver de mantenimiento;
              Asignada → Devolver; Disponible → Asignar */}
          {id &&
            !estaDadaDeBaja &&
            (data.estado === "En mantenimiento" ? (
              <button
                type="button"
                onClick={() => navigate("/almacen/mantenimiento")}
                className={estiloDeBoton("primario")}
              >
                <CornerDownLeft className="w-4 h-4" />
                Devolver de mantenimiento
              </button>
            ) : currentMovementId ? (
              <button
                type="button"
                onClick={() => navigate(`/almacen/asignacion/${id}/devolver`)}
                className={estiloDeBoton("primario")}
              >
                <CornerDownLeft className="w-4 h-4" />
                Devolver
              </button>
            ) : data.disponibilidad === "Disponible" ? (
              <button
                type="button"
                onClick={() => navigate(`/almacen/asignacion/${id}/reasignar`)}
                className={estiloDeBoton("primario")}
              >
                <User className="w-4 h-4" />
                Asignar
              </button>
            ) : null)}
          {/* Con una incidencia abierta no se puede reportar otra: el paso que
              sigue es cerrar la que ya existe, en la pantalla de incidencias.
              Mientras se comprueba no se ofrece ninguno de los dos, para no
              mostrar un boton que cambia de golpe. */}
          {!revisandoReporte &&
            !estaDadaDeBaja &&
            (reporteAbierto ? (
              <button
                type="button"
                onClick={() => navigate(`/almacen/reportes/${reporteAbierto.id}/ver`)}
                className={estiloDeBoton("primario")}
              >
                <FileWarning className="w-4 h-4" />
                Ver reporte
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowReportModal(true)}
                className={estiloDeBoton("primario")}
              >
                <FileWarning className="w-4 h-4" />
                Reportar
              </button>
            ))}

          {/* Acciones destructivas — separadas a la derecha por una línea */}
          <div
            className="flex items-center gap-2 pl-3 ml-1 border-l"
            style={{ borderColor: "#E0E0E0" }}
          >
            {id && canDarBaja && !estaDadaDeBaja && (
              <button
                type="button"
                onClick={() => setShowDarBajaModal(true)}
                className={estiloDeBoton("peligro")}
              >
                <Ban className="w-4 h-4" />
                Dar de baja
              </button>
            )}
            {id && canDarBaja && estaDadaDeBaja && (
              <button
                type="button"
                onClick={() => setShowDarAltaModal(true)}
                className={estiloDeBoton("exito")}
              >
                <RefreshCw className="w-4 h-4" />
                Dar de alta
              </button>
            )}
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={deleting}
              className={estiloDeBoton("peligro")}
            >
              <Trash2 className="w-4 h-4" />
              Eliminar
            </button>
          </div>
        </div>
      </div>

      {/* Información general */}
      <div
        className="bg-white rounded-xl border shadow-sm mb-6"
        style={{ borderColor: "#E0E0E0" }}
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: "#E0E0E0" }}>
          <h2 className="text-base font-semibold text-gray-800">
            Información general
          </h2>
        </div>
        <div className="p-6 space-y-6">
          {/* Grupo 1 — Identificación (único título sin línea) */}
          <div>
            <GroupTitle withLine={false}>Identificación</GroupTitle>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              <div>
                <p className="mb-1.5" style={FIELD_LABEL_STYLE}>
                  Código interno
                </p>
                <p style={FIELD_VALUE_STYLE}>
                  {data.internalCode || "Sin código interno"}
                </p>
              </div>
              <div>
                <p className="mb-1.5" style={FIELD_LABEL_STYLE}>
                  Número de serie
                </p>
                <p style={FIELD_VALUE_STYLE}>
                  {data.numeroSerie || "Sin número de serie"}
                </p>
              </div>
              <div>
                <p className="mb-1.5" style={FIELD_LABEL_STYLE}>
                  Estado
                </p>
                {data.estado ? (
                  <EstadoBadge estado={data.estado} />
                ) : (
                  <p style={FIELD_VALUE_STYLE}>Sin estado</p>
                )}
              </div>
              <div>
                <p className="mb-1.5" style={FIELD_LABEL_STYLE}>
                  Disponibilidad
                </p>
                {data.disponibilidad ? (
                  <DisponibilidadBadge value={data.disponibilidad} />
                ) : (
                  <p style={FIELD_VALUE_STYLE}>Sin disponibilidad</p>
                )}
              </div>
              <div>
                <p className="mb-1.5" style={FIELD_LABEL_STYLE}>
                  Registrada por
                </p>
                <p style={FIELD_VALUE_STYLE}>{data.registradaPor || "—"}</p>
              </div>
            </div>
          </div>

          {/* Grupo 2 — Características */}
          <div>
            <GroupTitle>Características</GroupTitle>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              <div>
                <p className="mb-1.5" style={FIELD_LABEL_STYLE}>
                  Categoría
                </p>
                <p style={FIELD_VALUE_STYLE}>
                  {data.categoria || "Sin categoría"}
                </p>
              </div>
              <div>
                <p className="mb-1.5" style={FIELD_LABEL_STYLE}>
                  Mantenimiento cada
                </p>
                <p style={FIELD_VALUE_STYLE}>
                  {!data.periodoMantenimiento
                    ? "Sin periodo de mantenimiento"
                    : data.periodoMantenimiento === "1"
                      ? "1 mes"
                      : `${data.periodoMantenimiento} meses`}
                </p>
              </div>
              {/* Descripción — abarca 2 columnas; si es muy larga, se ajusta en varias líneas */}
              <div className="col-span-2">
                <p className="mb-1.5" style={FIELD_LABEL_STYLE}>
                  Descripción
                </p>
                <p
                  className="break-words"
                  style={{ ...FIELD_VALUE_STYLE, lineHeight: "1.4" }}
                >
                  {data.descripcion || "Sin descripción"}
                </p>
              </div>
            </div>
          </div>

          {/* Grupo 3 — Asignación actual */}
          <div>
            <GroupTitle>Asignación actual</GroupTitle>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              <div>
                <p className="mb-1.5" style={FIELD_LABEL_STYLE}>
                  Proyecto
                </p>
                <p style={FIELD_VALUE_STYLE}>
                  {data.proyectoAsignado || "Sin proyecto"}
                </p>
              </div>
              <div>
                <p className="mb-1.5" style={FIELD_LABEL_STYLE}>
                  Almacén
                </p>
                <p style={FIELD_VALUE_STYLE}>{data.almacen || "Sin almacén"}</p>
              </div>
              {/* Responsable va al final y abarca 2 columnas: con la asignación
                  pendiente debajo cuelga el aviso, y en una columna sola se
                  partía en dos renglones. */}
              <div className="col-span-2">
                <p className="mb-1.5" style={FIELD_LABEL_STYLE}>
                  Responsable
                </p>
                <p style={FIELD_VALUE_STYLE}>
                  <ResponsableDeHerramienta
                    nombre={data.responsable || "Sin responsable"}
                    pendienteDe={data.pendienteDe}
                    estadoDeAprobacion={data.estadoDeAprobacion}
                  />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documentos e información adicional (CR-01) */}
      <DocumentosEnConsulta
        documentos={data.documentos}
        informacionAdicional={data.informacionAdicional}
        adjuntosDeMovimientos={adjuntosDeMovimientos}
      />

      {ultimaEntrega && (
        <UltimaEntrega
          estado={STATUS_LABEL[ultimaEntrega.resultingStatus] ?? ultimaEntrega.resultingStatus}
          descripcion={ultimaEntrega.notes ?? null}
          fotos={fotosDeLaEntrega}
          fotosDelReceptor={fotosDelReceptorEnLaEntrega}
          onAbrirFoto={(fotos, indice) => {
            setFotosDelViewer(fotos);
            setViewer({ tab: "evidencia", index: indice });
          }}
        />
      )}

      {ultimaEntrega && (
        <SelloDeAceptacion
          sello={ultimaEntrega}
          receptor={ultimaEntrega.responsibleUserName ?? null}
          className="mb-6"
        />
      )}

      {/* Tabs de fotos/evidencia — fuera de la caja, igual que en mantenimiento */}
      <div className="flex gap-1 mb-5">
        <button
          type="button"
          onClick={() => setPhotoTab("herramienta")}
          className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 ${
            photoTab === "herramienta"
              ? "border-[#477FFA]"
              : "border-transparent hover:border-gray-200"
          }`}
          style={{ color: "#1E1E1F" }}
        >
          Fotografías
        </button>
        <button
          type="button"
          onClick={() => setPhotoTab("evidencia")}
          className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 ${
            photoTab === "evidencia"
              ? "border-[#477FFA]"
              : "border-transparent hover:border-gray-200"
          }`}
          style={{ color: "#1E1E1F" }}
        >
          Última evidencia
        </button>
      </div>

      {/* Fotos / Evidencia */}
      <div
        className="bg-white rounded-xl border shadow-sm mb-6"
        style={{ borderColor: "#E0E0E0" }}
      >
        <div
          className="px-6 pt-5 pb-4"
          style={{ borderColor: "#E0E0E0", borderBottom: "1px solid #E0E0E0" }}
        >
          <h2 className="text-base font-semibold text-gray-800">
            {photoTab === "herramienta" ? "Fotografías" : "Última evidencia"}
          </h2>
        </div>

        {/* Contenido del tab activo */}
        <div className="p-6">
          {photoTab === "herramienta" ? (
            <div className="flex flex-wrap gap-4">
              {data.fotos.length > 0
                ? data.fotos.map((src, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() =>
                        setViewer({ tab: "herramienta", index: i })
                      }
                      className="desde-izq rounded-xl overflow-hidden border border-gray-200 bg-gray-50 flex-shrink-0 cursor-pointer hover:border-[#477FFA] transition-colors"
                      style={{ width: 150, height: 150, animationDelay: `${Math.min(i, 5) * 90}ms` }}
                    >
                      <img
                        src={src}
                        alt={`Foto ${i + 1}`}
                        className="w-full h-full object-contain"
                      />
                    </button>
                  ))
                : (
                    <p className="text-sm text-gray-400 italic">
                      Sin fotos adjuntas
                    </p>
                  )}
            </div>
          ) : loadingHistorial ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
              Cargando evidencia…
            </div>
          ) : historialError ? (
            <p className="text-sm text-gray-400 py-2">
              Sin permiso para cargar la evidencia fotográfica.
            </p>
          ) : (
            recentEvidenceFotos.length === 0 ? (
              <p className="text-sm text-gray-400 italic">
                Sin evidencia reciente
              </p>
            ) : (
              <div className="space-y-6">
                <GrupoDeEvidencia
                  titulo="Fotos de quien entrega"
                  urls={evidenciaDeQuienEntrega}
                  onVer={(url) => {
                    setFotosDelViewer(null);
                    setViewer({
                      tab: "evidencia",
                      index: recentEvidenceFotos.indexOf(url),
                    });
                  }}
                />

                {evidenciaDeQuienRecibe.length > 0 && (
                  <GrupoDeEvidencia
                    titulo="Fotos de quien recibe"
                    urls={evidenciaDeQuienRecibe}
                    onVer={(url) => {
                      setFotosDelViewer(null);
                      setViewer({
                        tab: "evidencia",
                        index: recentEvidenceFotos.indexOf(url),
                      });
                    }}
                  />
                )}
              </div>
            )
          )}
        </div>
      </div>

      {/* Movimientos recientes */}
      <div
        className="bg-white rounded-xl border shadow-sm"
        style={{ borderColor: "#E0E0E0" }}
      >
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: "#E0E0E0" }}
        >
          <h2 className="text-base font-semibold text-gray-800">
            Movimientos recientes
          </h2>
          {id && (
            <button
              type="button"
              onClick={goToHistorial}
              className="inline-flex items-center gap-1 text-sm font-medium text-[#315FB9] hover:underline"
            >
              Ver historial completo
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
        {loadingHistorial ? (
          <div className="flex items-center justify-center py-10">
            <p className="text-sm text-gray-400">Cargando historial...</p>
          </div>
        ) : historialError ? (
          <div className="flex items-center justify-center py-10">
            <p className="text-sm text-gray-400">
              No se pudo cargar el historial
            </p>
          </div>
        ) : historial.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <p className="text-sm text-gray-500">
              Aún no hay movimientos registrados
            </p>
          </div>
        ) : (
          <div className="p-6">
            <div
              className="border rounded-xl overflow-hidden"
              style={{ borderColor: "#E0E0E0" }}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr style={{ borderBottom: "1px solid #E0E0E0" }}>
                      {[
                        "Fecha y hora",
                        "Acción",
                        "Responsable",
                        "Realizado por",
                        "Proyecto",
                        "Condición al entregar",
                      ].map((h) => (
                        <th
                          key={h}
                          className="text-left py-3 px-4 text-sm font-semibold first:pl-6 whitespace-nowrap text-gray-800"
                        >
                          {h}
                        </th>
                      ))}
                      <th className="w-10 pr-6" />
                    </tr>
                  </thead>
                  <tbody>
                    {movimientosRecientes.map((row) => (
                      <tr
                        key={row.id}
                        onClick={goToHistorial}
                        className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                        style={{ borderBottom: "1px solid #E0E0E0" }}
                      >
                        <td className="py-3 px-4 pl-6 text-gray-500 whitespace-nowrap">
                          {new Date(row.createdAt).toLocaleString("es-MX", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            timeZone: ZONA_DE_OPERACION,
                          })}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          {/* Un retorno por rechazo en el verde de siempre se
                              leía como algo que salió bien (A-08). */}
                          <Badge color={row.rejectedMovementId ? "rojo" : "verde"}>
                            {accionDelMovimiento(
                              MOVEMENT_LABEL[row.type] ?? row.type,
                              row.rejectedMovementId,
                            )}
                          </Badge>
                          {row.decommissionReason && (
                            <p className="text-xs text-gray-500 mt-1">
                              {ETIQUETA_MOTIVO_BAJA[row.decommissionReason] ??
                                row.decommissionReason}
                            </p>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                          {row.responsibleUserName ?? "Sin responsable"}
                        </td>
                        <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                          {row.performedByUserName ?? "Sin registro"}
                        </td>
                        <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                          {row.projectName ?? "Sin proyecto"}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          <EstadoBadge
                            estado={
                              STATUS_LABEL[row.resultingStatus] ??
                              "En buen estado"
                            }
                          />
                        </td>
                        <td className="py-3 px-4 pr-6 text-right">
                          <ChevronRight className="w-4 h-4 text-gray-400 inline-block" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Historial de mantenimiento (entidad aparte de los movimientos) */}
      <div
        className="bg-white rounded-xl border shadow-sm mt-6"
        style={{ borderColor: "#E0E0E0" }}
      >
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: "#E0E0E0" }}
        >
          <h2 className="text-base font-semibold text-gray-800">
            Historial de mantenimiento
          </h2>
          <button
            type="button"
            onClick={() => navigate("/almacen/mantenimiento")}
            className="inline-flex items-center gap-1 text-sm font-medium text-[#315FB9] hover:underline"
          >
            Ver historial completo
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        {loadingMant ? (
          <div className="flex items-center justify-center py-10">
            <p className="text-sm text-gray-400">Cargando mantenimiento...</p>
          </div>
        ) : mantError ? (
          <div className="flex items-center justify-center py-10">
            <p className="text-sm text-gray-400">
              No se pudo cargar el mantenimiento
            </p>
          </div>
        ) : mantRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-sm text-gray-500">
              Sin registros de mantenimiento
            </p>
          </div>
        ) : (
          <div className="p-6">
            <div
              className="border rounded-xl overflow-hidden"
              style={{ borderColor: "#E0E0E0" }}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr style={{ borderBottom: "1px solid #E0E0E0" }}>
                      {[
                        "Tipo",
                        "Fecha de envío",
                        "Taller",
                        "Responsable",
                        "Realizado por",
                        "Estado",
                        "Núm. de serie",
                      ].map((h) => (
                        <th
                          key={h}
                          className="text-left py-3 px-4 text-sm font-semibold first:pl-6 last:pr-6 whitespace-nowrap text-gray-800"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mantRows.map((row) => (
                      <tr
                        key={row.id}
                        className="hover:bg-gray-50/50 transition-colors"
                        style={{ borderBottom: "1px solid #E0E0E0" }}
                      >
                        <td className="py-3 px-4 pl-6 text-gray-600 whitespace-nowrap">
                          {row.tipo}
                        </td>
                        <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                          {row.fechaEnvio}
                        </td>
                        <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                          {row.taller}
                        </td>
                        <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                          {row.responsable}
                        </td>
                        <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                          <div>{row.realizadoPor}</div>
                          {row.realizadoPorRetorno &&
                            row.realizadoPorRetorno !== row.realizadoPor && (
                              <div className="text-xs text-gray-400">
                                Retorno: {row.realizadoPorRetorno}
                              </div>
                            )}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          <Badge color={row.estado === "Reparado" ? "verde" : "amarillo"}>
                            {row.estado}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 pr-6 text-gray-500 whitespace-nowrap">
                          {data.numeroSerie || "Sin número de serie"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reporte de daño modal */}
      {showReportModal && (
        <ReporteModal
          toolId={id ?? ""}
          toolName={data.nombre}
          onClose={() => setShowReportModal(false)}
          onCreated={(reportId) => navigate(`/almacen/reportes/${reportId}/ver`)}
        />
      )}

      {/* Dar de baja modal */}
      {showDarBajaModal && id && (
        <DarBajaModal
          toolId={id}
          toolName={data.nombre}
          onClose={() => setShowDarBajaModal(false)}
          onDone={() => {
            setShowDarBajaModal(false);
            onBack?.();
          }}
        />
      )}

      {/* Dar de alta (reactivar) — desde aquí y no solo desde el listado: si
          estás viendo una herramienta dada de baja, reactivarla es lo único
          que puedes querer hacerle (A-09). */}
      {showDarAltaModal && id && (
        <DarAltaModal
          toolId={id}
          toolName={data.nombre}
          onClose={() => setShowDarAltaModal(false)}
          onDone={() => {
            setShowDarAltaModal(false);
            // La ficha entera cambia de estado, no solo un campo: se recarga
            // en vez de adivinar cómo quedó.
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

// Estado form → API enum
const ESTADO_TO_STATUS: Record<string, string> = {
  "En buen estado": "good",
  "Equipo nuevo": "new_equipment",
  "Falla/daño": "damaged",
  Faltante: "missing",
  "En mantenimiento": "in_maintenance",
};

// ─── Error → mensaje amigable ─────────────────────────────────────────────────

const VALIDATION_MAP: Record<string, string> = {
  "warehouseId must be a UUID": "Selecciona un almacén válido.",
  "warehouseId should not be empty": "El almacén es requerido.",
  "categoryId must be a UUID": "Selecciona una categoría válida.",
  "categoryId should not be empty": "La categoría es requerida.",
  "projectId must be a UUID": "Selecciona un proyecto válido.",
  "responsibleUserId must be a UUID": "Selecciona un responsable válido.",
  "name should not be empty": "El nombre es requerido.",
  "serialCode should not be empty": "El número de serie es requerido.",
  "serialCode must be unique":
    "Ya existe una herramienta con ese número de serie.",
  Conflict: "Ya existe una herramienta con ese número de serie.",
};

function friendlyError(err: unknown): string {
  const e = err as { statusCode?: number; message?: string | string[] };

  if (e.statusCode === 500) {
    return "Error del servidor. Inténtalo de nuevo o contacta al administrador.";
  }
  // El back responde 409 tanto por número de serie duplicado como por intentar
  // cambiar el Estado con una incidencia abierta. Aplastar los dos en el mismo
  // texto dejaba al usuario con un mensaje falso y sin saber qué hacer, así que
  // se respeta el mensaje del servidor cuando trae uno propio.
  if (e.statusCode === 409) {
    const raw = Array.isArray(e.message) ? e.message.join(" ") : e.message;
    if (raw && raw !== "Conflict") return VALIDATION_MAP[raw] ?? raw;
    return "Ya existe una herramienta con ese código o número de serie.";
  }

  const msgs: string[] = Array.isArray(e.message)
    ? e.message
    : [e.message ?? "Error al guardar"];
  const translated = msgs.map((m) => VALIDATION_MAP[m] ?? m);
  return translated.join(" ");
}

// ─── Create / Edit mode ───────────────────────────────────────────────────────

const EditMode = ({
  mode,
  id,
  initialData,
  onBack,
  onSaved,
  currentMovementId: _currentMovementId,
}: {
  mode: "create" | "edit";
  id?: string;
  initialData: FormData;
  onBack?: () => void;
  onSaved?: () => void;
  currentMovementId?: string | null;
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(initialData);
  const [photos, setPhotos] = useState<(string | null)[]>([
    initialData.fotos[0] ?? null,
    initialData.fotos[1] ?? null,
    initialData.fotos[2] ?? null,
  ]);
  const [photosUploading, setPhotosUploading] = useState<
    [boolean, boolean, boolean]
  >([false, false, false]);
  /**
   * Si esta herramienta lleva fotos de evidencia de su estado (CR-04).
   *
   * Apagarlo baja el almacenamiento en las que no lo ameritan. Al editar sale
   * como quedo guardado; en una herramienta nueva arranca encendido.
   */
  const [pideFotos, setPideFotos] = useState(initialData.pideFotos);
  /**
   * Documentos de la ficha (CR-01). Se editan en local y se aplican al Guardar,
   * igual que las fotos; lo que sube al momento es el archivo, no el vinculo.
   */
  const [documentos, setDocumentos] = useState<DocumentoAdjunto[]>(
    initialData.documentos,
  );
  const [documentosSubiendo, setDocumentosSubiendo] = useState(false);
  const [warehouses, setWarehouses] = useState<ApiWarehouse[]>([]);
  const [categories, setCategories] = useState<ApiToolCategory[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [projects, setProjects] = useState<ApiProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmAssign, setConfirmAssign] = useState<{
    projectName: string | null;
    userName: string;
    body: Record<string, unknown>;
  } | null>(null);
  // Si la herramienta tiene una incidencia abierta, el campo Estado se
  // bloquea: primero hay que resolverla desde el módulo de Reportes.
  const [openIncident, setOpenIncident] = useState<{
    id: string;
    folioDisplay: string;
  } | null>(null);

  useEffect(() => {
    if (!id) return;
    apiService
      .request<{ data: { id: string; folioDisplay: string }[] }>(
        `/almacen/reportes?toolId=${id}&status=open&limit=1`,
      )
      .then((res) => setOpenIncident(res.data?.[0] ?? null))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    apiService
      .request<ApiWarehouse[]>("/almacen/almacenes/public/all")
      .then((res) => setWarehouses(Array.isArray(res) ? res : []))
      .catch(() => toast.error("No se pudieron cargar los almacenes"));

    apiService
      .request<ApiToolCategory[]>("/almacen/categorias/public/all")
      .then((res) => setCategories(Array.isArray(res) ? res : []))
      .catch(() => {});

    // Solo quien tiene el modulo de Almacen: el resto no puede aprobar la
    // asignacion desde la app (A-04). Aqui no hace falta conservar a nadie
    // porque este select solo existe al dar de alta, donde todavia no hay
    // responsable previo.
    apiService
      .request<UserProfile[]>(RUTA_DE_RESPONSABLES)
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setUsers(
          [...list].sort((a, b) =>
            `${a.firstName} ${a.lastName}`.localeCompare(
              `${b.firstName} ${b.lastName}`,
              "es",
            ),
          ),
        );
      })
      .catch(() => {});
    apiService
      .request<ApiProject[] | { data: ApiProject[] }>("/projects/public/all")
      .then((res) => setProjects(Array.isArray(res) ? res : (res.data ?? [])))
      .catch(() => {});
  }, [mode]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // La disponibilidad solo es manipulable cuando la herramienta está en un
  // estado utilizable. Si no, queda forzada a "No disponible" (la define el estado).
  //
  // Son dos, no uno: equipo nuevo también sirve. Con solo "En buen estado" una
  // herramienta recién comprada nacía no disponible y no se podía asignar,
  // aunque el listado sí la mostrara disponible porque él sí mira los dos.
  const estadoEsUtilizable =
    !formData.estado ||
    formData.estado === "En buen estado" ||
    formData.estado === "Equipo nuevo";

  const handlePhoto = async (index: number, file: File) => {
    // Mostrar preview local mientras sube
    const localPreview = URL.createObjectURL(file);
    setPhotos((prev) => prev.map((p, i) => (i === index ? localPreview : p)));
    setPhotosUploading(
      (prev) =>
        prev.map((v, i) => (i === index ? true : v)) as [
          boolean,
          boolean,
          boolean,
        ],
    );

    try {
      const form = new globalThis.FormData();
      form.append("file", await comprimirImagen(file));
      const res = await apiService.request<{ url: string }>(
        "/almacen/upload/foto",
        {
          method: "POST",
          body: form,
        },
      );
      // Reemplazar blob:// con la URL real de S3
      URL.revokeObjectURL(localPreview);
      setPhotos((prev) => prev.map((p, i) => (i === index ? res.url : p)));
    } catch {
      // Si falla el upload, limpiar el preview
      URL.revokeObjectURL(localPreview);
      setPhotos((prev) => prev.map((p, i) => (i === index ? null : p)));
      toast.error("Error al subir la foto. Intenta de nuevo.");
    } finally {
      setPhotosUploading(
        (prev) =>
          prev.map((v, i) => (i === index ? false : v)) as [
            boolean,
            boolean,
            boolean,
          ],
      );
    }
  };

  const handleRemovePhoto = (index: number) =>
    setPhotos((prev) => prev.map((p, i) => (i === index ? null : p)));

  // ── Execute actual create (called directly or from confirm modal) ─────────────
  const executeCreate = async (body: Record<string, unknown>) => {
    // La asignación se dispara con el responsable; el proyecto es opcional.
    const willAssign = !!formData.responsable;

    // El back exige al menos una foto de evidencia al asignar, salvo que la
    // ficha traiga apagado el pedir fotos (CR-04).
    // Las fotos de la herramienta (ya subidas a S3) se reutilizan como evidencia.
    const realPhotos = photos.filter(
      (p) => p && !p.startsWith("blob:"),
    ) as string[];
    if (willAssign && pideFotos && realPhotos.length === 0) {
      toast.error(
        "Para asignar la herramienta al crearla debes subir al menos una fotografía.",
      );
      return;
    }

    setLoading(true);
    try {
      const created = await apiService.request<{ id: string }>(
        "/almacen/herramientas",
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
      // Si se seleccionó proyecto y responsable, asignar la herramienta de inmediato.
      // Se usan las mismas fotos de la herramienta como evidencia de la asignación inicial.
      if (willAssign && created?.id) {
        await apiService.request(
          `/almacen/herramientas/${created.id}/asignar`,
          {
            method: "POST",
            body: JSON.stringify({
              responsibleUserId: formData.responsable,
              ...(formData.proyectoAsignado && {
                projectId: formData.proyectoAsignado,
              }),
              ...(formData.almacen && { warehouseId: formData.almacen }),
              ...(realPhotos[0] && { evidenceUrl1: realPhotos[0] }),
              ...(realPhotos[1] && { evidenceUrl2: realPhotos[1] }),
              ...(realPhotos[2] && { evidenceUrl3: realPhotos[2] }),
            }),
          },
        );
      }
      onBack?.();
    } catch (err: unknown) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const executeEdit = async (body: Record<string, unknown>) => {
    // Edición: solo actualiza los datos de la herramienta. La asignación se
    // maneja aparte (flujo de Asignación) y el backend la conserva en el PATCH.
    setLoading(true);
    try {
      await apiService.request(`/almacen/herramientas/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      onSaved?.();
    } catch (err: unknown) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Un documento a medias todavia no tiene URL: guardar ahora lo perderia
    // sin avisar.
    if (documentosSubiendo) {
      toast.error("Espera a que terminen de subir los documentos.");
      return;
    }
    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido.");
      return;
    }
    if (!formData.categoria) {
      toast.error("Selecciona una categoría.");
      return;
    }
    if (!formData.almacen) {
      toast.error("Selecciona un almacén.");
      return;
    }

    const body: Record<string, unknown> = {
      name: formData.nombre.trim(),
      // Se envían explícitamente (null si están vacíos) para poder limpiarlos al editar
      serialCode: formData.numeroSerie.trim() || null,
      internalCode: formData.internalCode.trim() || null,
      categoryId: formData.categoria,
      warehouseId: formData.almacen,
      ...(formData.descripcion.trim() && {
        description: formData.descripcion.trim(),
      }),
      // Se mandan explicitamente (null y lista vacia) para que borrar persista
      // al editar, igual que las fotos.
      additionalInfo: formData.informacionAdicional.trim() || null,
      documents: documentos,
      ...(formData.estado && { status: ESTADO_TO_STATUS[formData.estado] }),
      // Disponibilidad: si está en buen estado la define el usuario; si no, va forzada a false
      isAvailable: estadoEsUtilizable
        ? formData.disponibilidad !== "No disponible"
        : false,
      // Mantenimiento periodico: solo por tiempo. Se envian null cuando la
      // herramienta no lleva periodo, para poder quitarlo al editar.
      maintenanceIntervalType: formData.periodoMantenimiento ? "calendar" : null,
      maintenanceIntervalValue: formData.periodoMantenimiento
        ? Number(formData.periodoMantenimiento) * DIAS_POR_MES // meses → dias
        : null,
      // El check solo marca si a esta herramienta se le piden fotos; no borra
      // las que ya tiene.
      requiresConditionPhotos: pideFotos,
      // Fotos — se envían explícitamente (null si se quitaron) para que el
      // borrado persista al editar. Un blob:// (subida en curso) se trata como null.
      photoUrl1:
        photos[0] && !photos[0].startsWith("blob:") ? photos[0] : null,
      photoUrl2:
        photos[1] && !photos[1].startsWith("blob:") ? photos[1] : null,
      photoUrl3:
        photos[2] && !photos[2].startsWith("blob:") ? photos[2] : null,
    };

    // El backend exige responsable para asignar; un proyecto solo no se puede guardar.
    if (formData.proyectoAsignado && !formData.responsable) {
      toast.error(
        "Para asignar un proyecto también debes seleccionar un responsable.",
      );
      return;
    }

    if (mode === "create") {
      // Hay asignación si se eligió responsable (el proyecto es opcional).
      if (formData.responsable) {
        const realPhotos = photos.filter((p) => p && !p.startsWith("blob:"));
        if (pideFotos && realPhotos.length === 0) {
          toast.error(
            "Para asignar la herramienta al crearla debes subir al menos una fotografía.",
          );
          return;
        }
        const proj = projects.find((p) => p.id === formData.proyectoAsignado);
        const user = users.find((u) => u.id === formData.responsable);
        setConfirmAssign({
          projectName: formData.proyectoAsignado
            ? (proj?.name ?? "proyecto seleccionado")
            : null,
          userName: user
            ? `${user.firstName} ${user.lastName}`
            : "responsable seleccionado",
          body,
        });
        return;
      }
      await executeCreate(body);
      return;
    }

    // ── Edit mode: solo actualiza los datos de la herramienta ──
    // El campo Estado ya viene bloqueado en la UI si hay una incidencia
    // abierta; esto es solo un respaldo por si igual llega un cambio.
    if (openIncident && formData.estado !== initialData.estado) {
      toast.error(
        `Esta herramienta tiene una incidencia abierta (${openIncident.folioDisplay}). Cierra la incidencia desde el módulo de Reportes antes de cambiar el Estado.`,
      );
      return;
    }

    await executeEdit(body);
  };

  const isEdit = mode === "edit";
  const title = isEdit
    ? `${formData.nombre || "Herramienta"} — Editar`
    : "Registrar herramienta";
  const subtitle = isEdit
    ? ""
    : "Da de alta aquí una herramienta nueva o existente";

  return (
    <div className="mx-auto px-6 py-8 max-w-7xl">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <button
          type="button"
          onClick={onBack}
          className={estiloDeBoton("secundario")}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          {subtitle && (
            <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Información general */}
        <div
          className="bg-white rounded-xl border shadow-sm"
          style={{ borderColor: "#E0E0E0" }}
        >
          <div
            className="px-6 py-4 border-b"
            style={{ borderColor: "#E0E0E0" }}
          >
            <h2 className="text-base font-semibold text-gray-800">
              Información general
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Nombre */}
              <div>
                <label
                  htmlFor="nombre"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  id="nombre"
                  name="nombre"
                  type="text"
                  placeholder="Nombre de la herramienta"
                  value={formData.nombre}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                />
              </div>

              {/* Categoría */}
              <div>
                <label
                  htmlFor="categoria"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Categoría <span className="text-red-500">*</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={categories.map((c) => ({ value: String(c.id), label: c.name }))}
                  value={formData.categoria}
                  onChange={(v) => handleChange(comoEventoDeCambio("categoria", v))}
                  placeholder="Seleccionar categoría"
                  id="categoria"
                  name="categoria"
                  placeholderDeBusqueda="Buscar categoría..."
                />
              </div>

              {/* Descripción */}
              <div className="sm:col-span-2">
                <label
                  htmlFor="descripcion"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Descripción
                </label>
                <textarea
                  id="descripcion"
                  name="descripcion"
                  rows={3}
                  placeholder="Describe brevemente la herramienta"
                  value={formData.descripcion}
                  onChange={handleChange}
                  className={`${INPUT_CLASS} resize-none`}
                />
              </div>

              {/* Número de serie */}
              <div>
                <label
                  htmlFor="numeroSerie"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Número de serie
                </label>
                <input
                  id="numeroSerie"
                  name="numeroSerie"
                  type="text"
                  placeholder="TL-DRL-0001"
                  value={formData.numeroSerie}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                />
              </div>

              {/* Código interno */}
              <div>
                <label
                  htmlFor="internalCode"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Código interno
                </label>
                <input
                  id="internalCode"
                  name="internalCode"
                  type="text"
                  placeholder="Ej. INV-001"
                  value={formData.internalCode}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                />
              </div>

              {/* Disponibilidad — editable solo si la herramienta está "En buen estado" */}
              <div>
                <label
                  htmlFor="disponibilidad"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Disponibilidad
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={DISPONIBILIDADES.map((d) => ({ value: d, label: d }))}
                  value={estadoEsUtilizable ? formData.disponibilidad || "Disponible" : "No disponible"}
                  onChange={(v) => handleChange(comoEventoDeCambio("disponibilidad", v))}
                  placeholder="Seleccionar"
                  id="disponibilidad"
                  name="disponibilidad"
                  disabled={!estadoEsUtilizable}
                  canDelete={false}
                  placeholderDeBusqueda="Buscar disponibilidad..."
                />
                {!estadoEsUtilizable && (
                  <p className="mt-1 text-xs text-gray-400">
                    Se determina automáticamente según el estado de la
                    herramienta.
                  </p>
                )}
              </div>

              {/* Estado */}
              <div>
                <label
                  htmlFor="estado"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Estado
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={ESTADOS.map((e) => ({ value: e, label: e }))}
                  value={formData.estado}
                  onChange={(v) => handleChange(comoEventoDeCambio("estado", v))}
                  placeholder="Seleccionar estado"
                  id="estado"
                  name="estado"
                  disabled={!!openIncident}
                  placeholderDeBusqueda="Buscar estado..."
                />
                {openIncident && (
                  <p className="mt-1 text-xs text-red-500">
                    Esta herramienta tiene una incidencia abierta (
                    {openIncident.folioDisplay}). Para cambiar su estado,
                    primero cierra la incidencia desde el módulo de{" "}
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/almacen/reportes/${openIncident.id}/ver`)
                      }
                      className="font-semibold text-[#315FB9] hover:underline"
                    >
                      Reportes
                    </button>
                    .
                  </p>
                )}
              </div>

              {/* Mantenimiento periodico: solo por tiempo. El selector de tipo
                  (calendario u horas de uso) se retiro a peticion de negocio:
                  el mantenimiento se define por periodo, y la app movil ya lo
                  hacia asi. Sin periodo = la herramienta no lleva mantenimiento
                  periodico. */}
              <div>
                <label
                  htmlFor="periodoMantenimiento"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Periodo de mantenimiento
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={[{ value: "", label: "Sin mantenimiento periódico" }, ...PERIODOS_MANTENIMIENTO.map((m) => ({ value: m, label: m === "1" ? "1 mes" : `${m} meses` }))]}
                  value={formData.periodoMantenimiento}
                  onChange={(v) => handleChange(comoEventoDeCambio("periodoMantenimiento", v))}
                  placeholder="Sin mantenimiento periódico"
                  id="periodoMantenimiento"
                  name="periodoMantenimiento"
                  placeholderDeBusqueda="Buscar periodo..."
                />
              </div>

              {/* Almacén */}
              <div>
                <label
                  htmlFor="almacen"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Almacén <span className="text-red-500">*</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={warehouses.map((w) => ({ value: String(w.id), label: w.name }))}
                  value={formData.almacen}
                  onChange={(v) => handleChange(comoEventoDeCambio("almacen", v))}
                  placeholder="Seleccionar almacén"
                  id="almacen"
                  name="almacen"
                  placeholderDeBusqueda="Buscar almacén..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Documentos e información adicional (CR-01) */}
        <SeccionDocumentos
          documentos={documentos}
          onDocumentosChange={setDocumentos}
          informacionAdicional={formData.informacionAdicional}
          onInformacionAdicionalChange={(valor) =>
            setFormData((prev) => ({ ...prev, informacionAdicional: valor }))
          }
          onSubiendoChange={setDocumentosSubiendo}
        />

        {/* Asignación (opcional) — solo al dar de alta; en edición la asignación
            se maneja únicamente vía el flujo de Asignación */}
        {!isEdit && (
        <div
          className="bg-white rounded-xl border shadow-sm"
          style={{ borderColor: "#E0E0E0" }}
        >
          <div
            className="px-6 py-4 border-b"
            style={{ borderColor: "#E0E0E0" }}
          >
            <h2 className="text-base font-semibold text-gray-800">
              Asignación (opcional)
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Proyecto asignado */}
              <div>
                <label
                  htmlFor="proyectoAsignado"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Proyecto asignado
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={[{ value: "", label: "Sin asignación" }, ...projects.map((p) => ({ value: String(p.id), label: p.name }))]}
                  value={formData.proyectoAsignado}
                  onChange={(v) => handleChange(comoEventoDeCambio("proyectoAsignado", v))}
                  placeholder="Sin asignación"
                  id="proyectoAsignado"
                  name="proyectoAsignado"
                  placeholderDeBusqueda="Buscar proyecto..."
                />
              </div>
              {/* Responsable */}
              <div>
                <label
                  htmlFor="responsable"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Responsable
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={[{ value: "", label: "Sin responsable" }, ...users.map((u) => ({ value: String(u.id), label: `${u.firstName} ${u.lastName}` }))]}
                  value={formData.responsable}
                  onChange={(v) => handleChange(comoEventoDeCambio("responsable", v))}
                  placeholder="Sin responsable"
                  id="responsable"
                  name="responsable"
                  placeholderDeBusqueda="Buscar responsable..."
                />
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Fotografías */}
        <div
          className="bg-white rounded-xl border shadow-sm"
          style={{ borderColor: "#E0E0E0" }}
        >
          <div
            className="px-6 py-4 border-b"
            style={{ borderColor: "#E0E0E0" }}
          >
            <h2 className="text-base font-semibold text-gray-800">
              Fotografías
            </h2>
          </div>
          <div className="p-6">
            <Checkbox
              checked={pideFotos}
              onChange={(e) => setPideFotos(e.target.checked)}
              etiqueta="Pedir fotos de evidencia al mover esta herramienta"
            />
            <p className="mb-4 ml-6 text-sm text-gray-500">
              Apagado, dejan de pedirse al asignar, devolver y reportar. Las que ya
              tenga se conservan, y aquí siempre puedes agregar o quitar.
            </p>

            <div className="flex flex-wrap gap-4">
              {photos.map((preview, i) => (
                <PhotoCard
                  key={i}
                  preview={preview}
                  uploading={photosUploading[i]}
                  onFile={(file) => handlePhoto(i, file)}
                  onRemove={() => handleRemovePhoto(i)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className={estiloDeBoton("secundario")}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className={estiloDeBoton("primario")}
          >
            <Save className="w-4 h-4" />
            {loading ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>

      {/* ── Confirmación de asignación (crear y editar) ── */}
      {confirmAssign && (
        <CapaDeModal>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 relative">
            {/* Cerrar */}
            <button
              type="button"
              onClick={() => setConfirmAssign(null)}
              disabled={loading}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <X className="w-4 h-4" />
            </button>

            <h3 className="text-lg font-bold text-gray-900 mb-1 pr-8">
              Confirmar asignación
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              Al guardar esta herramienta quedará asignada con los siguientes
              datos. Podrás cambiarlo más adelante
            </p>

            {/* Datos */}
            <div className="space-y-3 mb-6">
              {confirmAssign.projectName && (
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3">
                  <div className="w-10 h-10 rounded-full bg-[#E0E7F5] flex items-center justify-center flex-shrink-0">
                    <Folder className="w-5 h-5" style={{ color: "#315FB9" }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">Proyecto</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {confirmAssign.projectName}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3">
                <div className="w-10 h-10 rounded-full bg-[#E0E7F5] flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5" style={{ color: "#315FB9" }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">Responsable</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {confirmAssign.userName}
                  </p>
                </div>
              </div>
            </div>

            {/* Acciones */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmAssign(null)}
                disabled={loading}
                className={estiloDeBoton("secundario")}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  const body = confirmAssign.body;
                  setConfirmAssign(null);
                  if (mode === "create") executeCreate(body);
                  else executeEdit(body);
                }}
                disabled={loading}
                className={estiloDeBoton("primario")}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 girando" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckCheck className="w-4 h-4" />
                    Confirmar y asignar
                  </>
                )}
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}
    </div>
  );
};

// ─── API response → FormData mapper ──────────────────────────────────────────

const STATUS_TO_ESTADO: Record<string, EstadoHerramienta> = {
  good: "En buen estado",
  new_equipment: "Equipo nuevo",
  damaged: "Falla/daño",
  in_maintenance: "En mantenimiento",
  missing: "Faltante",
  decommissioned: "Dada de baja",
};

interface ToolApiResponse {
  /** CR-03: cómo va la aceptación de la asignación vigente. */
  currentApprovalStatus?: "pending" | "approved" | "rejected" | null;
  /** A quien se le asigno y todavia no acepta (CR-03). */
  pendingResponsibleUserName?: string | null;
  id: string;
  name: string;
  serialCode: string;
  internalCode: string | null;
  status: string;
  isAvailable: boolean;
  description: string | null;
  categoryId: string;
  categoryName: string | null;
  warehouseId: string;
  warehouseName: string | null;
  requiresConditionPhotos?: boolean;
  additionalInfo?: string | null;
  documents?: DocumentoGuardado[];
  photoUrl1: string | null;
  photoUrl2: string | null;
  photoUrl3: string | null;
  responsibleUserId: string | null;
  responsibleUserName: string | null;
  createdByUserName: string | null;
  projectId: string | null;
  projectName: string | null;
  currentMovementId: string | null;
  maintenanceIntervalType: string | null;
  maintenanceIntervalValue: number | null;
  createdAt: string;
}

/** Form data with IDs — used by EditMode selects */
function apiToFormData(dto: ToolApiResponse): FormData {
  const estado: EstadoHerramienta =
    STATUS_TO_ESTADO[dto.status] ?? "En buen estado";
  const disponibilidad: Disponibilidad = dto.isAvailable
    ? "Disponible"
    : "No disponible";
  const fotos = [dto.photoUrl1, dto.photoUrl2, dto.photoUrl3].filter(
    Boolean,
  ) as string[];
  // Mantenimiento periodico por tiempo: el back guarda dias, la UI maneja meses.
  // Solo se acepta si el valor coincide con una opcion del select; cualquier otra
  // cosa (incluido un intervalo viejo por horas de uso) queda sin periodo, para
  // no mostrar un dato que el formulario ya no sabe expresar.
  const mesesDesdeDias =
    dto.maintenanceIntervalType === "calendar" &&
    dto.maintenanceIntervalValue != null
      ? String(Math.round(dto.maintenanceIntervalValue / DIAS_POR_MES))
      : "";
  const periodoMantenimiento = PERIODOS_MANTENIMIENTO.includes(mesesDesdeDias)
    ? mesesDesdeDias
    : "";
  return {
    nombre: dto.name,
    registradaPor: dto.createdByUserName ?? "—",
    estadoDeAprobacion: dto.currentApprovalStatus,
    pendienteDe: dto.pendingResponsibleUserName,
    categoria: dto.categoryId, // UUID — for EditMode <select>
    descripcion: dto.description ?? "",
    numeroSerie: dto.serialCode,
    internalCode: dto.internalCode ?? "",
    disponibilidad,
    estado,
    proyectoAsignado: dto.projectId ?? "", // UUID — for EditMode <select>
    responsable: dto.responsibleUserId ?? "", // UUID — for EditMode <select>
    almacen: dto.warehouseId, // UUID — for EditMode <select>
    periodoMantenimiento,
    fotos,
    pideFotos: dto.requiresConditionPhotos ?? true,
    documentos: dto.documents ?? [],
    informacionAdicional: dto.additionalInfo ?? "",
    createdAt: dto.createdAt,
  };
}

/** Display data with human-readable names — used by ViewMode */
function apiToViewData(dto: ToolApiResponse): FormData {
  return {
    ...apiToFormData(dto),
    categoria: dto.categoryName ?? dto.categoryId,
    almacen: dto.warehouseName ?? dto.warehouseId,
    proyectoAsignado: dto.projectName ?? "Sin proyecto",
    responsable: dto.responsibleUserName ?? "Sin responsable",
    registradaPor: dto.createdByUserName ?? "—",
  };
}

// ─── Main component ───────────────────────────────────────────────────────────

const HerramientaForm = ({ mode, id, onBack }: HerramientaFormProps) => {
  const location = useLocation();
  const prefill =
    mode === "create"
      ? (
          location.state as {
            prefill?: { nombre?: string; almacen?: string };
          } | null
        )?.prefill
      : undefined;

  const [currentMode, setCurrentMode] = useState<Mode>(mode);
  const [editData, setEditData] = useState<FormData | null>(
    prefill
      ? {
          ...EMPTY_FORM,
          nombre: prefill.nombre ?? "",
          almacen: prefill.almacen ?? "",
        }
      : null,
  );
  const [viewData, setViewData] = useState<FormData | null>(null); // names for display
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(mode !== "create");
  const [currentMovementId, setCurrentMovementId] = useState<string | null>(
    null,
  );

  // Fetch real tool data when viewing or editing
  const fetchTool = useCallback(() => {
    if (!id) return;
    setFetching(true);
    setFetchError(null);
    apiService
      .request<ToolApiResponse>(`/almacen/herramientas/${id}`)
      .then((dto) => {
        setEditData(apiToFormData(dto));
        setViewData(apiToViewData(dto));
        setCurrentMovementId(dto.currentMovementId ?? null);
      })
      .catch(() => setFetchError("No se pudo cargar la herramienta"))
      .finally(() => setFetching(false));
  }, [id]);

  useEffect(() => {
    if (mode === "create" || !id) {
      setFetching(false);
      return;
    }
    fetchTool();
  }, [id, mode, fetchTool]);

  // Validation errors shown as static messages (hooks must run before returns)
  const propError =
    (mode === "view" || mode === "edit") && !id
      ? `Error: se requiere un ID para el modo "${mode}".`
      : mode === "create" && id
        ? 'Error: el modo "create" no debe recibir un ID.'
        : null;

  if (propError) {
    return (
      <div className="mx-auto px-6 py-8">
        <p className="text-sm text-red-500 font-medium">{propError}</p>
      </div>
    );
  }

  // A-01: silueta de la ficha en vez del texto suelto de "Cargando".
  if (fetching) {
    return (
      <div className="mx-auto px-6 py-8">
        <EsqueletoDetalle campos={9} />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="mx-auto px-6 py-8">
        <p className="text-sm text-red-500 font-medium">{fetchError}</p>
      </div>
    );
  }

  if (currentMode === "view") {
    return (
      <ViewMode
        data={viewData ?? { ...EMPTY_FORM }}
        id={id}
        onBack={onBack}
        onEdit={() => setCurrentMode("edit")}
        currentMovementId={currentMovementId}
        warehouseId={editData?.almacen ?? ""}
      />
    );
  }

  return (
    <EditMode
      mode={currentMode === "create" ? "create" : "edit"}
      id={id}
      initialData={editData ?? { ...EMPTY_FORM }}
      onBack={currentMode === "edit" ? () => setCurrentMode("view") : onBack}
      onSaved={
        currentMode === "edit"
          ? () => {
              fetchTool();
              setCurrentMode("view");
            }
          : undefined
      }
      currentMovementId={currentMovementId}
    />
  );
};

export default HerramientaForm;
